/**
 * CompanyPopover Component
 *
 * Popover yang menampilkan informasi detail perusahaan saat user mengklik
 * nama perusahaan di Info Bar.
 *
 * Informasi yang ditampilkan:
 * - Nama Perusahaan (header)
 * - NPWP
 * - Jenis Usaha (Dagang, Manufaktur, Jasa) sebagai tags
 * - Alamat
 * - Link ke Company Profile
 */

import { useState } from 'react';
import { Popover, Tag, Button, Space, Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/modules/auth/authStore';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';

const { Text, Title } = Typography;

interface CompanyPopoverProps {
  children: ReactNode;
}

export function CompanyPopover({ children }: CompanyPopoverProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const {
    NamaPerusahaan,
    NPWPPerusahaan,
    AlamatPerusahaan,
    sebagaiPerusahaanDagang,
    sebagaiPerusahaanManufaktur,
    sebagaiPerusahaanJasa,
  } = useAuthStore();

  // Cek apakah ada jenis usaha yang dipilih
  const hasJenisUsaha = sebagaiPerusahaanDagang || sebagaiPerusahaanManufaktur || sebagaiPerusahaanJasa;

  const content = (
    <div style={{ width: 280 }}>
      {/* Header */}
      <Title level={5} style={{ marginBottom: 16, marginTop: 0 }}>
        {NamaPerusahaan || '-'}
      </Title>

      {/* NPWP */}
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          NPWP
        </Text>
        <div>{NPWPPerusahaan || '-'}</div>
      </div>

      {/* Jenis Usaha */}
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
          Jenis Usaha
        </Text>
        <div>
          {hasJenisUsaha ? (
            <Space size={4} wrap>
              {sebagaiPerusahaanDagang && <Tag color="blue">Dagang</Tag>}
              {sebagaiPerusahaanManufaktur && <Tag color="purple">Manufaktur</Tag>}
              {sebagaiPerusahaanJasa && <Tag color="cyan">Jasa</Tag>}
            </Space>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </div>
      </div>

      {/* Alamat */}
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          Alamat
        </Text>
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {AlamatPerusahaan || '-'}
        </div>
      </div>

      {/* Link ke Company Profile */}
      <Button
        type="link"
        size="small"
        onClick={() => {
          setOpen(false);
          navigate('/pengaturan/company-profile');
        }}
        style={{ padding: 0 }}
      >
        Lihat Profil Lengkap <RightOutlined />
      </Button>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
    >
      {children}
    </Popover>
  );
}
